import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { LifecycleBar, sectionPath } from "@/components/job/Lifecycle";
import { Sparkles, UserCheck, FileCheck2, CalendarClock, FlaskConical, Rocket, PenLine, ArrowRight } from "lucide-react";

const ICONS = { screening: UserCheck, test: FlaskConical, publish: Rocket, build: PenLine, interviews: CalendarClock, edit: FileCheck2 };
const TONES = { amber: "bg-amber-400/12 text-amber-600 dark:text-amber-300", blue: "bg-sky-400/12 text-sky-600 dark:text-sky-300", green: "bg-primary/12 text-primary" };
const ACTION = { screening: "Review", test: "Open", publish: "Publish", build: "Finish", interviews: "Open" };

export function KpiTile({ value, label, sub, subTone, highlight, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={cn("hs-card text-left flex items-center gap-4 px-5 py-4 transition-[border-color,transform] duration-200 hover:-translate-y-0.5", highlight ? "border-amber-400/40 hover:border-amber-400/60" : "hover:border-foreground/15")}>
      <span className={cn("text-[30px] font-display font-semibold leading-none tabular-nums", highlight && "text-amber-500 dark:text-amber-300")}>{value}</span>
      <span className="min-w-0 leading-tight">
        <span className="block text-sm text-foreground/80">{label}</span>
        {sub && <span className={cn("block text-sm truncate", subTone === "green" ? "text-primary" : highlight ? "text-amber-600 dark:text-amber-300" : "text-muted-foreground")}>{sub}</span>}
      </span>
    </button>
  );
}

export function buildNeeds(jobs, interviews, signoffs = []) {
  const items = jobs.filter((j) => ["amber", "blue"].includes(j.lifecycle?.next?.tone) && j.lifecycle.next.section !== "interviews").map((j) => {
    const nx = j.lifecycle.next;
    const sub = nx.section === "screening" ? `${j.title} · scored and ranked` : nx.section === "test" ? `${j.title} · assessment` : j.title;
    return { id: j.id, icon: ICONS[nx.section] || PenLine, tone: "amber", title: nx.label, sub, action: ACTION[nx.section] || "Open", url: sectionPath(j.id, nx.section) };
  });
  jobs.filter((j) => j.lifecycle?.to_review && j.lifecycle.next?.section !== "screening").forEach((j) => {
    const n = j.lifecycle.to_review;
    items.unshift({ id: `${j.id}-review`, icon: UserCheck, tone: "amber", title: `${n} applicant${n === 1 ? "" : "s"} ready to review`, sub: `${j.title} · scored and ranked`, action: "Review", url: `/recruiter/pipeline?job=${j.id}` });
  });
  signoffs.forEach((r) => items.unshift({ id: `signoff-${r.trial_id}`, icon: FileCheck2, tone: "amber", title: `Review the ${r.job_title || "role"} assessment`, sub: `${r.requested_by || "A teammate"} asked for your review`, action: "Review", url: `/recruiter/trial-report/${r.trial_id}` }));
  const soon = interviews.filter((i) => i.status === "scheduled" && Date.parse(i.when) > Date.now() - 36e5).sort((a, b) => Date.parse(a.when) - Date.parse(b.when)).slice(0, 2);
  soon.forEach((i) => {
    const d = new Date(i.when);
    const today = d.toDateString() === new Date().toDateString();
    const when = today ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
    items.push({ id: i.id, icon: CalendarClock, tone: "blue", title: `Interview with ${i.candidate_name} ${today ? "at" : "on"} ${when}`, sub: `${i.job_title}${i.kit_name ? " · kit ready" : ""}`, action: "Open kit", url: "/recruiter/interviews" });
  });
  return items;
}

export function NeedsYou({ items, brief }) {
  const nav = useNavigate();
  const [showBrief, setShowBrief] = useState(false);
  return (
    <section className="hs-card overflow-hidden flex flex-col" aria-label="Needs you" data-testid="needs-you">
      <header className="flex items-center justify-between px-5 py-4 border-b border-border">
        <h2 className="text-[15px] font-semibold">Needs you · {items.length}</h2>
        {brief?.headline && <button onClick={() => setShowBrief((v) => !v)} aria-expanded={showBrief} className="flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground" data-testid="daily-brief-toggle"><Sparkles className="h-3.5 w-3.5 text-primary" /> Today&apos;s brief</button>}
      </header>
      {showBrief && <p className="px-5 py-3 text-sm text-foreground/85 bg-primary/[0.05] border-b border-border leading-relaxed" data-testid="daily-brief-headline">{brief.headline}</p>}
      {items.length === 0 ? (
        <div className="flex-1 px-5 py-10 text-center text-sm text-muted-foreground" data-testid="needs-you-empty">Nothing is waiting on you. New applicants and approvals appear here.</div>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <li key={it.id + i} className="flex items-center gap-3.5 px-5 py-3.5" data-testid={`needs-item-${i}`}>
                <span className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0", TONES[it.tone])}><Icon className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block text-sm font-semibold truncate">{it.title}</span>
                  <span className="block text-[13px] text-muted-foreground truncate">{it.sub}</span>
                </span>
                <button onClick={() => nav(it.url)} className="shrink-0 text-[13px] font-semibold px-3.5 py-2 rounded-lg bg-foreground/[0.06] hover:bg-foreground/[0.1] transition-colors" data-testid={`needs-action-${i}`}>{it.action}</button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function ActiveRoles({ jobs }) {
  const nav = useNavigate();
  const list = [...jobs].sort((a, b) => (b.lifecycle?.step || 0) - (a.lifecycle?.step || 0) || (b.lifecycle?.to_review || 0) - (a.lifecycle?.to_review || 0)).slice(0, 5);
  return (
    <section className="hs-card overflow-hidden" aria-label="Active roles" data-testid="active-roles">
      <header className="flex items-center justify-between px-5 py-4 border-b border-border">
        <h2 className="text-[15px] font-semibold">Active roles</h2>
        <button onClick={() => nav("/recruiter/jobs")} className="text-[13px] text-primary hover:underline inline-flex items-center gap-1" data-testid="view-all-jobs">All jobs <ArrowRight className="h-3 w-3" /></button>
      </header>
      <ul className="divide-y divide-border">
        {list.map((j) => {
          const lc = j.lifecycle || {};
          const caption = j.status !== "published" ? lc.next?.label : `${lc.total || 0} candidate${lc.total === 1 ? "" : "s"}${lc.to_review ? ` · ${lc.to_review} to review` : ""}`;
          return (
            <li key={j.id}>
              <button onClick={() => nav(sectionPath(j.id, lc.next?.section || "build"))} className="w-full text-left px-5 py-3.5 space-y-2 hover:bg-foreground/[0.03] transition-colors" data-testid={`workspace-card-${j.id}`}>
                <span className="flex items-baseline justify-between gap-3"><span className="text-sm font-semibold truncate">{j.title}</span><span className="text-xs text-muted-foreground shrink-0">{lc.label}</span></span>
                <LifecycleBar step={lc.step || 1} />
                <span className="block text-xs text-muted-foreground truncate">{caption}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
